import cx from 'classnames'
import { useEffect, useState, useRef } from 'react'
import { isNil } from 'lodash'

import { PLACEHOLDER_USER_COVER_IMAGE } from 'lib/image'
import { MAX_IMAGE_DIMENSION } from 'config'

import './style.scss'

const ImageLoader = ({
  url,
  placeholderImage,
  placeholderImageTrans,
  alt,
  className,
  style,
  onClick,
  freezeGif,
  disableViewportCheck,
  enableRetry,
  preserveHeight,
}: {
  url: string
  placeholderImage?: string
  placeholderImageTrans?: boolean
  alt?: string
  className?: string
  style?: object
  onClick?: Function
  freezeGif?: boolean
  disableViewportCheck?: boolean
  enableRetry?: boolean
  preserveHeight?: string
}) => {
  const observer = useRef<IntersectionObserver>()
  const ___$el = useRef<HTMLImageElement>()
  const ___src = useRef<string | undefined>()
  const ___isUnmounted = useRef<boolean>(false)

  const [__data, __dataSet] = useState({
    isActive: false,
    src: null,
  })

  const [__isVisibleInViewport, __isVisibleInViewportSet] = useState(
    disableViewportCheck ? true : false
  )

  // Ordinary `IntersectionObserver` for watching scroll events. Nothing fancy here.
  useEffect(() => {
    if (!__isVisibleInViewport) {
      observer.current = new IntersectionObserver(
        (elements) => {
          const $target = elements?.[0]

          if ($target?.intersectionRatio > 0) {
            observer.current.disconnect()
            __isVisibleInViewportSet(true)
          }
        },
        {
          rootMargin: '50px 0px',
          threshold: [0, 0.25, 0.5, 0.75, 1], // I've found this makes scroll detection much more stable.
        }
      )

      if (observer.current && ___$el.current) {
        observer.current.observe(___$el.current)
      }
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [])

  useEffect(() => {
    // `url` may be `undefined` from trying to get an unfinished fetching properties.
    // For example, an attempt to get `user?.profile_icon_thumbnail` in `.UserMenu__handler`.
    // In this case we shall treat its state as "not ready for image loading", thus do nothing.
    if (url === undefined) {
      return
    }

    // `url` maybe `null`, but that means it's intentionally not set by the user, in which case
    // we will use placeholder and proceed as "ready for image loading".
    if (url === null) {
      ___src.current = placeholderImage ?? PLACEHOLDER_USER_COVER_IMAGE
      return
    }

    // As long as `url` is not `undefined` nor `null`, that means the property already finished loading.
    if (url !== ___src.current) {
      ___src.current = url
    }
  }, [url])

  useEffect(() => {
    // We will only perform "image loading" only when it's "ready".
    // `___src.current` is set by `useEffect` above.
    let _retries = 0
    let _timeoutId: number

    if (__isVisibleInViewport && ___src.current !== undefined) {
      const _src = ___src.current

      const _img = new Image()
      _img.src = _src

      _img.onload = () => {
        if (___isUnmounted.current) {
          return
        }

        const naturalWidth = _img.naturalWidth
        const naturalHeight = _img.naturalHeight

        if (
          _src?.includes?.('.gif') &&
          (freezeGif ||
            naturalWidth > MAX_IMAGE_DIMENSION || // https://nanameue.slack.com/archives/CQX0L2X17/p1680819902750739
            naturalHeight > MAX_IMAGE_DIMENSION)
        ) {
          const $canvas = document.createElement('canvas')
          const w = ($canvas.width = naturalWidth)
          const h = ($canvas.height = naturalHeight)

          $canvas.getContext('2d').drawImage(_img, 0, 0, w, h)

          // Cross-domain, mimic original with all its tag attributes.
          for (let j = 0, a: any; (a = ___$el?.current?.attributes?.[j]); j++) {
            $canvas.setAttribute(a.name, a.value)
          }

          ___$el.current?.parentNode?.replaceChild?.($canvas, ___$el.current)
        } else {
          __dataSet({
            isActive: true,
            src: _src,
          })
        }
      }

      _img.onerror = () => {
        if (_retries < 3 && enableRetry && _src) {
          _timeoutId = window.setTimeout(() => {
            _img.src = _src
          }, 300)

          _retries = _retries + 1

          return
        }

        if (___isUnmounted.current) {
          return
        }

        __dataSet({
          isActive: true,
          src:
            _src === placeholderImage
              ? PLACEHOLDER_USER_COVER_IMAGE
              : placeholderImage ?? PLACEHOLDER_USER_COVER_IMAGE,
        })
      }
    }

    return () => {
      window.clearTimeout(_timeoutId)
    }
  }, [__isVisibleInViewport, url])

  useEffect(() => {
    return () => {
      // A safeguard preventing `__dataSet` being called
      // if the component has already been unmounted
      // but the `_img` had not finished loading yet.
      ___isUnmounted.current = true
    }
  }, [])

  const isActivePlaceholder =
    __data.src == placeholderImage || __data.src == PLACEHOLDER_USER_COVER_IMAGE

  const sharedProps = {
    className: cx('ImageLoader', {
      [className]: !isNil(className),
      'ImageLoader--placeholder-trans':
        isActivePlaceholder && placeholderImageTrans,
    }),
    'data-active': __data.isActive,
    'data-active-placeholder': isActivePlaceholder,
    'data-original-src': `${url}`,
    'data-original-placeholder': `${placeholderImage}`,
    style,
    onClick: () => {
      onClick?.()
    },
  }

  if (!__data.isActive) {
    return (
      <img
        ref={___$el}
        src={PLACEHOLDER_USER_COVER_IMAGE}
        {...sharedProps}
        style={
          preserveHeight
            ? {
                ...style,
                height: preserveHeight,
              }
            : style
        }
      />
    )
  }

  return <img src={__data.src} alt={alt} {...sharedProps} />
}

export default ImageLoader
